import { Link } from 'react-router-dom';

import { observer } from 'mobx-react-lite';

import { Badge } from '@components/badge';
import { Card } from '@components/card';
import { IconAssigneeSmall } from '@components/icons/assignee-small';
import { IconFilesSmall } from '@components/icons/files-small';
import { IconMessagesSmall } from '@components/icons/messages-small';
import { IconNotesSmall } from '@components/icons/notes-small';
import { IconRatingSmall } from '@components/icons/rating-small';
import { IconTasklistSmall } from '@components/icons/tasklist-small';
import { Stack } from '@components/stack';
import { StatusLine } from '@components/status-line';
import { Text } from '@components/text';
import { useIsMobile } from '@hooks/use-is-mobile';
import { abbreviateDate } from '@utils/date';
import { abbreviate } from '@utils/strings';

import { ApplicationLabels } from 'components/ApplicationLabels';
import { useStore } from 'models/Provider';
import { IApplication } from 'models/types';

export const LargeApplicationCard = observer(({ application }: { application: IApplication }) => {
  const { t, userStore, lang } = useStore();
  const isMobile = useIsMobile();
  const assignedTo = application.assignedTo && userStore.items.get(application.assignedTo);
  const date = abbreviateDate(application.date, lang);

  return (
    <Link to={`/jobs/${application.jobId}/application/${application.id}`}>
      <Card space={3}>
        <Stack space={0} orientation="horizontal" justify="space-between">
          <Stack space={2} orientation="horizontal">
            {application.isHired && <StatusLine variant="green" />}
            {application.isRejected && <StatusLine variant="red" />}
            <Stack space={1}>
              <Text font="semibold14" text={application.jobTitle} />
              <Text
                font="regular14"
                color="purple80"
                text={`${t('job.applicants.label.applied')}: ${date}`}
              />
              <Text
                font="regular14"
                color="purple80"
                text={`${t('job.applicants.label.stage')}: ${application.jobStageName}`}
              />
              <Stack space={2} orientation="horizontal">
                {application.numReviews > 0 && (
                  <Stack space={1} orientation="horizontal" align="center">
                    <IconRatingSmall color="purple50" />
                    <Badge text={application.numReviews} />
                  </Stack>
                )}
                {application.numMessages > 0 && (
                  <Stack space={1} orientation="horizontal" align="center">
                    <IconMessagesSmall color="purple50" />
                    <Badge text={application.numMessages} />
                  </Stack>
                )}
                {application.numNotes > 0 && (
                  <Stack space={1} orientation="horizontal" align="center">
                    <IconNotesSmall color="purple50" />
                    <Badge text={application.numNotes} />
                  </Stack>
                )}
                {application.numAttachments > 0 && (
                  <Stack space={1} orientation="horizontal" align="center">
                    <IconFilesSmall color="purple50" />
                    <Badge text={application.numAttachments} />
                  </Stack>
                )}
                {application.assignedTo && (
                  <Stack space={1} orientation="horizontal" align="center">
                    <IconAssigneeSmall color="purple50" />
                    <Badge text={abbreviate(assignedTo.name)} />
                  </Stack>
                )}
                {application.taskListItems &&
                  application.taskListItems.length > 0 &&
                  application.taskListItems[0].total > 0 && (
                    <Stack space={1} orientation="horizontal" align="center">
                      <IconTasklistSmall color="purple50" />
                      <Badge
                        text={`${application.taskListItems[0].checked} / ${application.taskListItems[0].total}`}
                      />
                    </Stack>
                  )}
              </Stack>
            </Stack>
          </Stack>
          {!isMobile && <ApplicationLabels application={application} />}
        </Stack>
      </Card>
    </Link>
  );
});
